import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Spin } from "@arco-design/web-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { endpoints } from "../../api/endpoints";
import { User } from "../../models/Person";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { IntakeShell } from "./components/IntakeShell";
import {
  isIntakeProfileComplete,
  mapOccupationToJourney,
} from "./services/intakeProfile";
import { IntakeProfileContext, IntakeProfileState } from "./context/IntakeProfileContext";
import { JourneyType } from "./types/interviewSession";

type GateStatus = "loading" | "login_required" | "profile_incomplete" | "ready";

type Props = {
  children: React.ReactNode;
  skipGate?: boolean;
};

export function IntakeProfileGate({ children, skipGate }: Props) {
  const { t } = useTranslation();
  const loggedUser = useSelector((state: RootState) => state.user.user);
  const isSurveyForm = useSelector(
    (state: RootState) => state.config.isSurveyForm
  );

  const [status, setStatus] = useState<GateStatus>(
    skipGate ? "ready" : "loading"
  );
  const [profile, setProfile] = useState<User | null>(null);
  const [profileState, setProfileState] = useState<IntakeProfileState>({
    journey: "corporate",
    profile: null,
    isSurveyForm,
  });

  useEffect(() => {
    if (skipGate) {
      return;
    }

    if (!loggedUser?.id) {
      setStatus("login_required");
      return;
    }

    let cancelled = false;

    const load = async () => {
      setStatus("loading");
      try {
        const response = await axios.get<User>(endpoints.currentProfile);
        if (cancelled) return;

        const data = response.data;
        setProfile(data);

        if (!isIntakeProfileComplete(data, isSurveyForm)) {
          setStatus("profile_incomplete");
          return;
        }

        const journey = mapOccupationToJourney(
          data.occupationStatus
        ) as JourneyType;

        setProfileState({
          journey,
          profile: data,
          isSurveyForm,
        });
        setStatus("ready");
      } catch {
        if (!cancelled) {
          setStatus("profile_incomplete");
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [loggedUser?.id, isSurveyForm, skipGate]);

  if (skipGate) {
    return (
      <IntakeProfileContext.Provider value={profileState}>
        {children}
      </IntakeProfileContext.Provider>
    );
  }

  if (status === "loading") {
    return (
      <IntakeShell centerContent>
        <Spin size={32} />
        <p className="intake-flow__subtext intake-flow__subtext--center">
          {t("intake.profile_gate.loading")}
        </p>
      </IntakeShell>
    );
  }

  if (status === "login_required") {
    return (
      <IntakeShell centerContent>
        <h1 className="intake-flow__headline">
          {t("intake.profile_gate.login_title")}
        </h1>
        <p className="intake-flow__subtext intake-flow__subtext--center">
          {t("intake.profile_gate.login_body")}
        </p>
        <Link to={routes.LOGIN}>
          <Button className="intake-flow__cta" size="large" type="primary">
            {t("intake.profile_gate.login_button")}
          </Button>
        </Link>
      </IntakeShell>
    );
  }

  if (status === "profile_incomplete") {
    return (
      <IntakeShell centerContent>
        <h1 className="intake-flow__headline">
          {t("intake.profile_gate.incomplete_title")}
        </h1>
        <p className="intake-flow__subtext intake-flow__subtext--center">
          {t("intake.profile_gate.incomplete_body")}
        </p>
        <Link to={routes.profile}>
          <Button className="intake-flow__cta" size="large" type="primary">
            {t("intake.profile_gate.profile_button")}
          </Button>
        </Link>
      </IntakeShell>
    );
  }

  return (
    <IntakeProfileContext.Provider
      value={{
        journey: profileState.journey,
        profile: profile ?? profileState.profile,
        isSurveyForm,
      }}
    >
      {children}
    </IntakeProfileContext.Provider>
  );
}
